'use client';

import type { Location, MediaResponseDto, Property, PropertyAmenity, Valuation } from '@/lib/api/clients/properties';
import type { Listing } from '@/lib/api/clients/listings';
import {
  PropertyVirtualTour,
  PropertyVideos,
  PropertyFloorPlans,
  PropertyDocuments,
  groupMediaByContext,
} from '@/components/properties/PropertyMedia';
import { PropertyDetailTracker } from '@/components/properties/PropertyDetailTracker';
import { PropertySpecifications } from '@/components/properties/PropertySpecifications';
import { PropertyValuations } from '@/components/properties/PropertyValuations';
import {
    AllysPropertyAmenities,
    AllysPropertyContactCard,
    AllysPropertyFeatures,
    AllysPropertyGallery,
    AllysPropertyHero,
    AllysPropertyLocation,
    AllysPropertyOverview,
} from '@routeroyal/theme-allys';

type Props = {
  property: Property;
  media: MediaResponseDto[];
  amenities: PropertyAmenity[];
  valuations?: Valuation[];
  listing?: Listing | null;
  price: number | null;
  currency: string;
  locale: string;
  messages: Record<string, unknown>;
  contact: { email?: string; phone?: string; whatsapp?: string };
  location?: Location | null | undefined;
  basePath?: string;
  listingAgent?: {
    id: string;
    name: string;
    email: string;
    avatar: string | null;
    jobTitle: string | null;
  } | null;
};

export function PropertyDetailContentAllys({ 
  property, 
  media, 
  amenities,
  valuations = [],
  listing,
  price,
  currency,
  locale,
  messages,
  contact,
  location,
  basePath: _basePath,
  listingAgent,
}: Props) {
  const propertyDetail = messages.propertyDetail as Record<string, Record<string, string>>;

  // Group media by context if available
  const mediaByContext = property.mediaLinks 
    ? groupMediaByContext(property.mediaLinks)
    : {
        PRIMARY_HERO: [],
        GALLERY: [],
        FLOOR_PLAN: [],
        VIDEO: [],
        VIRTUAL_TOUR: [],
        DOCUMENT: [],
        OTHER: [],
      };

  // Extract first brochure from documentLinks (Document system)
  const brochureLink = property.documentLinks?.find(
    (link) => link.document?.type === 'BROCHURE'
  );
  const brochureDocumentId = brochureLink?.documentId ?? undefined;

  // Extract floor plans from media links - use the nested media object directly
  const floorPlansFromLinks = (mediaByContext.FLOOR_PLAN || []).map(m => m.media);

  // Build a deduplicated media set for the hero (images + videos)
  const heroImages = (() => {
    const seen = new Set<string>();
    const result: MediaResponseDto[] = [];

    const addIfNew = (item: MediaResponseDto) => {
      const key = item.id || item.url;
      if (key && !seen.has(key)) {
        seen.add(key);
        if (item.url) seen.add(item.url);
        result.push(item);
      }
    };

    // Images from the flat media array (excluding videos -- they're added below)
    for (const m of media) {
      if (m.mediaType === 'VIDEO' || m.mimeType?.startsWith('video/')) continue;
      addIfNew(m);
    }

    // Gallery images from mediaLinks
    for (const link of mediaByContext.GALLERY || []) {
      if (link.media) {
        addIfNew(link.media);
      }
    }

    // Append videos so they appear in the hero gallery
    for (const link of mediaByContext.VIDEO || []) {
      if (link.media) {
        addIfNew(link.media);
      }
    }

    return result;
  })();

  // Determine the primary image URL, and filter it out of heroImages to avoid duplication
  const primaryImageUrl = property.heroMedia?.url || property.primaryImageUrl || null;
  const heroImagesDeduped = primaryImageUrl
    ? heroImages.filter(img => img.url !== primaryImageUrl)
    : heroImages;

  // Build address from location data
  const propertyAddress = location?.addressLine1 
    || [location?.streetNumber, location?.streetName].filter(Boolean).join(' ')
    || location?.name
    || '';
  const propertyCity = location?.city || location?.localityName || location?.district || location?.administrativeAreaName || location?.suburb || '';
  const propertyCountry = location?.country || location?.countryName || '';

  // Get whatsapp number - prefer explicit whatsapp, fallback to phone
  const whatsappNumber = contact.whatsapp || contact.phone || '';

  const locationLabel = [location?.city || location?.localityName || location?.district || location?.administrativeAreaName || location?.suburb, location?.country || location?.countryName].filter(Boolean).join(', ') || null;

  return (
    <main className="min-h-screen bg-white">
      <PropertyDetailTracker
        propertyId={property.id}
        propertyName={property.name}
        price={price}
        currency={currency}
        {...(listing?.id ? { listingId: listing.id } : {})}
        {...(listing?.listingType ? { listingType: listing.listingType } : {})}
        bedrooms={property.bedrooms}
        bathrooms={property.bathrooms}
        location={locationLabel}
        {...(listingAgent?.name ? { agentName: listingAgent.name } : {})}
      />

      {/* Hero Gallery */}
      <AllysPropertyHero
        title={property.name}
        primaryImage={primaryImageUrl}
        images={heroImagesDeduped}
      />

      {/* Main Content */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-20 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
          {/* Left Column - Property Details */}
          <div className="lg:col-span-2 space-y-10">
            <div data-track-section="overview">
              <AllysPropertyOverview
                property={property}
                price={price}
                currency={currency}
                listingType={listing?.listingType || null}
                listingStatus={listing?.status || null}
                mainHeadline={listing?.mainHeadline || null}
                displayPriceText={listing?.displayPriceText || null}
                messages={{
                  overview: {
                    title: propertyDetail?.overview?.title || 'Overview',
                    description: propertyDetail?.overview?.description || 'Description',
                    status: propertyDetail?.overview?.status || 'Status',
                    propertyId: propertyDetail?.overview?.propertyId || 'Property ID',
                  },
                  pricing: {
                    askingPrice: propertyDetail?.pricing?.askingPrice || 'Asking Price',
                    contactForPrice: propertyDetail?.pricing?.contactForPrice || 'Price on request',
                  },
                }}
              />
            </div>

            <div className="border-t border-gray-200" />

            <div data-track-section="features">
              <AllysPropertyFeatures
                property={property}
                messages={{
                  keyFeatures: {
                    title: propertyDetail?.keyFeatures?.title || 'Key Features',
                    bedrooms: propertyDetail?.keyFeatures?.bedrooms || 'Bedrooms',
                    bathrooms: propertyDetail?.keyFeatures?.bathrooms || 'Bathrooms',
                    area: propertyDetail?.keyFeatures?.area || 'Floor Area',
                    landArea: propertyDetail?.keyFeatures?.landArea || 'Land Area',
                    yearBuilt: propertyDetail?.keyFeatures?.yearBuilt || 'Year Built',
                    yearRenovated: propertyDetail?.keyFeatures?.yearRenovated || 'Renovated',
                    parking: propertyDetail?.keyFeatures?.parking || 'Parking',
                    storeys: propertyDetail?.keyFeatures?.storeys || 'Storeys',
                    floorNumber: propertyDetail?.keyFeatures?.floorNumber || 'Floor Number',
                  },
                }}
              />
            </div>

            {/* Listing Details (auction, rental terms, sale info) */}
            {listing && (listing.auctionDetails || listing.rentalDetails || listing.saleDetails?.isNegotiable) && (
              <>
                <div className="border-t border-gray-200" />
                <div className="font-inter">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-1 h-8 bg-gradient-to-b from-[#BBA339] to-[#E4CE4A] rounded-full" />
                    <h2 className="text-xl md:text-2xl font-bold text-[#222222]">
                      {listing.auctionDetails ? 'Auction Details' : listing.rentalDetails ? 'Rental Details' : 'Sale Details'}
                    </h2>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {listing.auctionDetails?.auctionDate && (
                      <div className="p-4 rounded-xl border border-gray-100">
                        <p className="text-sm text-[#717171] font-medium mb-1">Auction Date</p>
                        <p className="text-base font-semibold text-[#222222]">
                          {new Date(listing.auctionDetails.auctionDate).toLocaleDateString(locale, { dateStyle: 'long' })}
                        </p>
                      </div>
                    )}
                    {listing.auctionDetails?.auctionType && (
                      <div className="p-4 rounded-xl border border-gray-100">
                        <p className="text-sm text-[#717171] font-medium mb-1">Auction Type</p>
                        <p className="text-base font-semibold text-[#222222] capitalize">
                          {listing.auctionDetails.auctionType.toLowerCase().replace(/_/g, ' ')}
                        </p>
                      </div>
                    )}
                    {listing.auctionDetails?.auctionLocation && (
                      <div className="p-4 rounded-xl border border-gray-100">
                        <p className="text-sm text-[#717171] font-medium mb-1">Auction Location</p>
                        <p className="text-base font-semibold text-[#222222]">{listing.auctionDetails.auctionLocation}</p>
                      </div>
                    )}
                    {listing.rentalDetails?.furnishedStatus && (
                      <div className="p-4 rounded-xl border border-gray-100">
                        <p className="text-sm text-[#717171] font-medium mb-1">Furnished</p>
                        <p className="text-base font-semibold text-[#222222] capitalize">
                          {listing.rentalDetails.furnishedStatus.toLowerCase().replace(/_/g, ' ')}
                        </p>
                      </div>
                    )}
                    {listing.rentalDetails?.minLeaseTermMonths && (
                      <div className="p-4 rounded-xl border border-gray-100">
                        <p className="text-sm text-[#717171] font-medium mb-1">Minimum Lease</p>
                        <p className="text-base font-semibold text-[#222222]">
                          {listing.rentalDetails.minLeaseTermMonths} month{listing.rentalDetails.minLeaseTermMonths !== 1 ? 's' : ''}
                        </p>
                      </div>
                    )}
                    {listing.rentalDetails?.petPolicy && listing.rentalDetails.petPolicy !== 'NO_PETS' && (
                      <div className="p-4 rounded-xl border border-gray-100">
                        <p className="text-sm text-[#717171] font-medium mb-1">Pet Policy</p>
                        <p className="text-base font-semibold text-[#222222] capitalize">
                          {listing.rentalDetails.petPolicy.toLowerCase().replace(/_/g, ' ')}
                        </p>
                      </div>
                    )}
                    {listing.rentalDetails?.depositPrice?.amount && (
                      <div className="p-4 rounded-xl border border-gray-100">
                        <p className="text-sm text-[#717171] font-medium mb-1">Deposit</p>
                        <p className="text-base font-semibold text-[#222222]">
                          {listing.rentalDetails.depositPrice.currency} {listing.rentalDetails.depositPrice.amount.toLocaleString()}
                        </p>
                      </div>
                    )}
                    {listing.saleDetails?.isNegotiable && (
                      <div className="p-4 rounded-xl border border-gray-100">
                        <p className="text-sm text-[#717171] font-medium mb-1">Price</p>
                        <p className="text-base font-semibold text-[#222222]">Negotiable</p>
                      </div>
                    )}
                    {listing.availableDate && (
                      <div className="p-4 rounded-xl border border-gray-100">
                        <p className="text-sm text-[#717171] font-medium mb-1">Available From</p>
                        <p className="text-base font-semibold text-[#222222]">
                          {new Date(listing.availableDate).toLocaleDateString(locale, { dateStyle: 'long' })}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Property Specifications - Enhanced Data */}
            {(property.condition || property.aspect || property.viewType || property.titleType || property.zoningCategory) && (
              <>
                <div className="border-t border-gray-200" />
                <div className="font-inter">
                  <PropertySpecifications
                    property={property}
                    messages={{
                      title: propertyDetail?.specifications?.title || 'Property Specifications',
                      subtitle: propertyDetail?.specifications?.subtitle || 'Detailed property information',
                    }}
                  />
                </div>
              </>
            )}

            <div className="border-t border-gray-200" />

            <div data-track-section="amenities">
              <AllysPropertyAmenities
                amenities={amenities}
                messages={{
                  amenities: {
                    title: propertyDetail?.amenities?.title || 'Amenities & Features',
                    subtitle: propertyDetail?.amenities?.subtitle || 'What this property offers',
                    noAmenities: propertyDetail?.amenities?.noAmenities || 'No amenities listed',
                  },
                }}
              />
            </div>

            {/* Virtual Tours */}
            {mediaByContext.VIRTUAL_TOUR && mediaByContext.VIRTUAL_TOUR.length > 0 && (
              <>
                <div className="border-t border-gray-200" />
                <div className="font-inter">
                  <PropertyVirtualTour tours={mediaByContext.VIRTUAL_TOUR} />
                </div>
              </>
            )}

            {/* Videos */}
            {mediaByContext.VIDEO && mediaByContext.VIDEO.length > 0 && (
              <>
                <div className="border-t border-gray-200" />
                <div className="font-inter">
                  <PropertyVideos videos={mediaByContext.VIDEO} />
                </div>
              </>
            )}

            {/* Floor Plans from media links */}
            {mediaByContext.FLOOR_PLAN && mediaByContext.FLOOR_PLAN.length > 0 && (
              <>
                <div className="border-t border-gray-200" />
                <div className="font-inter">
                  <PropertyFloorPlans floorPlans={mediaByContext.FLOOR_PLAN} />
                </div>
              </>
            )}

            {/* Gallery */}
            {heroImages.length > 0 && (
              <>
                <div className="border-t border-gray-200" />
                <div data-track-section="gallery">
                  <AllysPropertyGallery
                    media={heroImages}
                    floorPlans={floorPlansFromLinks}
                    models3D={[]}
                    propertyName={property.name}
                    messages={{
                      gallery: {
                        title: propertyDetail?.gallery?.title || 'Gallery',
                        subtitle: propertyDetail?.gallery?.subtitle || 'Explore this property',
                        viewAll: propertyDetail?.gallery?.viewAll || 'View All Photos',
                        noImages: propertyDetail?.gallery?.noImages || 'No images available',
                      },
                    }}
                  />
                </div>
              </>
            )}

            {/* Valuations */}
            {valuations.length > 0 && (
              <>
                <div className="border-t border-gray-200" />
                <div className="font-inter">
                  <PropertyValuations valuations={valuations} locale={locale} />
                </div>
              </>
            )}

            {/* Documents */}
            {mediaByContext.DOCUMENT && mediaByContext.DOCUMENT.length > 0 && (
              <>
                <div className="border-t border-gray-200" />
                <div className="font-inter">
                  <PropertyDocuments documents={mediaByContext.DOCUMENT} />
                </div>
              </>
            )}

            <div className="border-t border-gray-200" />

            <div data-track-section="location">
              <AllysPropertyLocation
                address={propertyAddress}
                city={propertyCity}
                country={propertyCountry}
                latitude={location?.latitude ?? 0}
                longitude={location?.longitude ?? 0}
                messages={{
                  location: {
                    title: propertyDetail?.location?.title || 'Location',
                    subtitle: propertyDetail?.location?.subtitle || 'Where you\'ll be',
                    getDirections: propertyDetail?.location?.getDirections || 'Get Directions',
                    nearby: propertyDetail?.location?.nearby || 'What\'s nearby',
                  },
                }}
              />
            </div>
          </div>

          {/* Right Column - Contact Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6" data-track-section="contact">
              <AllysPropertyContactCard
                propertyId={property.id}
                propertyName={property.name}
                propertyPrice={price}
                contactInfo={{
                  phone: contact.phone || '',
                  email: contact.email || '',
                  whatsapp: whatsappNumber,
                }}
                listingAgent={listingAgent ?? null}
                authUrl={`/${locale}/register`}
                brochureDocumentId={brochureDocumentId}
                organisationId={property.organisationId}
                messages={{
                  contact: {
                    title: propertyDetail?.contact?.title || 'Interested in this property?',
                    subtitle: propertyDetail?.contact?.subtitle || 'Get in touch for more information or to schedule a viewing.',
                    requestInfo: propertyDetail?.contact?.requestInfo || 'Request Information',
                    callUs: propertyDetail?.contact?.callUs || 'Call Us',
                    emailUs: propertyDetail?.contact?.emailUs || 'Email Us',
                    whatsapp: propertyDetail?.contact?.whatsapp || 'WhatsApp',
                    downloadBrochure: propertyDetail?.contact?.downloadBrochure || 'Download Brochure',
                    shareProperty: propertyDetail?.contact?.shareProperty || 'Share',
                    saveProperty: propertyDetail?.contact?.saveProperty || 'Save',
                    saved: propertyDetail?.contact?.saved || 'Saved',
                    ...(propertyDetail?.contact?.scheduleMeeting || listingAgent
                      ? { scheduleMeeting: propertyDetail?.contact?.scheduleMeeting || `Meet ${listingAgent?.name}` }
                      : {}),
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
